import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useAddBlacklistOrDnd } from '../api';
import type { DndAddTarget } from '../types';

export function ManageBlacklistDndPage() {
  const canAdd = usePermission('Number Action.Manage Blacklist-DND', BASE_PERMISSION.ADD);
  const { mutate, isPending } = useAddBlacklistOrDnd();
  const [mobile, setMobile] = useState('');
  const [target, setTarget] = useState<DndAddTarget>('BLACKLIST');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = mobile.trim();
    if (!/^\d{10}$/.test(trimmed)) {
      toast.error('Enter a valid 10-digit mobile number.');
      return;
    }
    mutate(target, trimmed, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Added to ${target.toLowerCase()}`);
        setMobile('');
      },
      onError: () => toast.error(`Could not add to ${target.toLowerCase()}.`),
    });
  };

  if (!canAdd) return <Navigate to="/voice-blacklist" replace />;

  return (
    <ScrollPage>
      <PageHeader
        title="Blacklist & DND"
        description="Add a single mobile number to the blacklist or the DND registry."
      />

      <Card className="sm:max-w-lg">
        <CardHeader>
          <CardTitle>Add number</CardTitle>
          <CardDescription>
            Pick the list to add the number to. Numbers already present are
            silently skipped.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="mobile">
                Mobile<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="mobile"
                inputMode="numeric"
                maxLength={10}
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))
                }
                placeholder="10-digit mobile number"
                autoFocus
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="target">
                Add to<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Select
                value={target}
                onValueChange={(v) => setTarget(v as DndAddTarget)}
              >
                <SelectTrigger id="target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BLACKLIST">Blacklist</SelectItem>
                  <SelectItem value="DND">DND</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Add
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
